interface Options {
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onTranscript: (text: string) => void;
  onCancel: () => void;
  onSend: (text: string) => void;
}

export class SpeechRecorder {
  private recognition: SpeechRecognition | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private stream: MediaStream | null = null;
  private animationFrame: number | null = null;
  private sampleInterval: number | null = null;
  private finalTranscript = "";
  private interimTranscript = "";
  private options: Options;
  private waveformHistory: number[] = [];
  private scrollOffset = 0;
  private canvasWidth = 0;
  private canvasHeight = 0;
  private ctx: CanvasRenderingContext2D | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private pendingCallback: (() => void) | null = null;

  constructor(options: Options) {
    this.options = options;
  }

  start() {
    const API = window.SpeechRecognition ?? window.webkitSpeechRecognition;
    if (!API) throw new Error("Speech recognition not supported");

    // Start recognition before the waveform's getUserMedia/AudioContext setup
    // so capture begins immediately instead of waiting on the audio pipeline.
    this.recognition = new API();
    this.recognition.continuous = true;
    this.recognition.interimResults = true;
    this.recognition.lang = "en-US";

    this.recognition.onresult = (event) => {
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result?.[0]) continue;
        if (result.isFinal) {
          this.finalTranscript += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      this.interimTranscript = interim;
    };

    this.recognition.onerror = (event) => {
      if (
        event.error === "not-allowed" ||
        event.error === "service-not-allowed"
      )
        this.options.onCancel();
    };
    this.recognition.onend = () => {
      if (this.pendingCallback) {
        this.pendingCallback();
        this.pendingCallback = null;
      }
    };
    try {
      this.recognition.start();
    } catch {}

    const canvas = this.options.canvasRef.current;
    if (canvas) this.startVisualization(canvas);

    document.addEventListener("keydown", this.handleKeyDown);
  }

  private handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      this.send();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      this.cancel();
    }
  };

  private get transcript() {
    return this.finalTranscript + this.interimTranscript;
  }

  send() {
    this.pendingCallback = () => {
      this.options.onTranscript(this.transcript);
      this.options.onSend(this.transcript);
    };
    this.stop();
  }

  cancel() {
    this.pendingCallback = () => {
      this.options.onTranscript(this.transcript);
      this.options.onCancel();
    };
    this.stop();
  }

  private startVisualization(canvas: HTMLCanvasElement) {
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    this.canvasWidth = rect.width;
    this.canvasHeight = rect.height;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    this.ctx = canvas.getContext("2d");
    if (this.ctx) this.ctx.scale(dpr, dpr);
    this.canvas = canvas;

    this.drawWaveform();
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        this.stream = stream;
        this.audioContext = new AudioContext();
        this.analyser = this.audioContext.createAnalyser();
        this.analyser.fftSize = 256;
        this.audioContext
          .createMediaStreamSource(stream)
          .connect(this.analyser);
        this.sampleInterval = window.setInterval(() => this.sampleAudio(), 100);
      })
      .catch(() => {});
  }

  private sampleAudio() {
    if (!this.analyser) return;
    const dataArray = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(dataArray);
    let maxAmplitude = 0;
    for (const value of dataArray) {
      const amplitude = Math.abs(value - 128) / 128;
      if (amplitude > maxAmplitude) maxAmplitude = amplitude;
    }
    this.waveformHistory.push(maxAmplitude);
  }

  private drawWaveform() {
    if (!this.ctx || !this.canvas) return;

    this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

    const barColor = getComputedStyle(this.canvas).color;
    const barWidth = 1;
    const barGap = 1.5;
    const barStep = barWidth + barGap;
    const barCount = Math.floor(this.canvasWidth / barStep);
    const centerY = this.canvasHeight / 2;

    // Smoothly animate scroll toward target (history length)
    const targetOffset = this.waveformHistory.length;
    this.scrollOffset += (targetOffset - this.scrollOffset) * 0.03;

    const fadeZone = 20;
    for (let i = 0; i < barCount; i++) {
      const historyIndex = Math.floor(this.scrollOffset) - barCount + i;
      const amplitude =
        historyIndex >= 0 ? this.waveformHistory[historyIndex] : 0;
      const barHeight =
        Math.min((amplitude ?? 0) * 10, 1) * (this.canvasHeight * 0.8) + 2;
      const fractional = this.scrollOffset % 1;
      const x = i * barStep - fractional * barStep;
      const y = centerY - barHeight / 2;

      const distFromRight = this.canvasWidth - x;
      const distFromLeft = x;
      const alpha = Math.min(
        distFromRight / fadeZone,
        distFromLeft / fadeZone,
        1
      );
      this.ctx.globalAlpha = alpha;
      this.ctx.fillStyle = barColor;

      this.ctx.beginPath();
      this.ctx.roundRect(x, y, barWidth, barHeight, barWidth / 2);
      this.ctx.fill();
    }

    this.animationFrame = requestAnimationFrame(() => this.drawWaveform());
  }

  stop() {
    document.removeEventListener("keydown", this.handleKeyDown);
    this.recognition?.stop();
    if (this.animationFrame) cancelAnimationFrame(this.animationFrame);
    if (this.sampleInterval) clearInterval(this.sampleInterval);
    this.audioContext?.close().catch(() => {});
    for (const track of this.stream?.getTracks() ?? []) track.stop();
  }
}
